import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";
import {
  ArrowLeft,
  AlertTriangle,
  Send,
  Loader2,
  CheckCircle2,
  Copy,
  Search,
  Headphones,
} from "lucide-react";
import { BETEC_CONTACT } from "../components/Contacto";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ee87a7b5-4234-4e1a-ae00-61e47c3e4eb8/artifacts/czcapx37_LOGO.png";

const EQUIPMENT_OPTIONS = [
  { value: "pos", label: "POS · Punto de Venta" },
  { value: "autoservicio", label: "Autoservicio · Kiosco" },
  { value: "carteleria", label: "Cartelería Digital" },
  { value: "turnomatico", label: "Turnomático" },
  { value: "redes", label: "Redes / Fibra Óptica" },
  { value: "otro", label: "Otro / No estoy seguro" },
];

const SEVERITY_OPTIONS = [
  {
    value: "critico",
    label: "Crítico",
    sub: "Operación detenida en sucursal(es)",
    color: "#dc2626",
  },
  {
    value: "alto",
    label: "Alto",
    sub: "Afecta operación pero hay workaround",
    color: "#ea580c",
  },
  {
    value: "medio",
    label: "Medio",
    sub: "Falla parcial · sin impacto inmediato",
    color: "#ca8a04",
  },
  {
    value: "bajo",
    label: "Bajo",
    sub: "Consulta o solicitud no urgente",
    color: "#16a34a",
  },
];

const initial = {
  requester_name: "",
  company: "",
  email: "",
  phone: "",
  branch: "",
  equipment: "pos",
  severity: "medio",
  subject: "",
  description: "",
};

export default function Soporte() {
  const [mode, setMode] = useState("create"); // 'create' | 'check'
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [createdTicket, setCreatedTicket] = useState(null);

  // status check
  const [statusInput, setStatusInput] = useState("");
  const [statusResult, setStatusResult] = useState(null);
  const [statusErr, setStatusErr] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Ensure page loads scrolled to top so form fields are visible
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.requester_name.trim() || form.requester_name.trim().length < 2)
      er.requester_name = "Ingresa tu nombre.";
    if (!form.company.trim() || form.company.trim().length < 2)
      er.company = "Ingresa el nombre de tu empresa.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      er.email = "Correo no válido.";
    if (!form.subject.trim() || form.subject.trim().length < 3)
      er.subject = "Describe brevemente el asunto.";
    if (!form.description.trim() || form.description.trim().length < 10)
      er.description = "Describe la incidencia (mínimo 10 caracteres).";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Revisa los campos requeridos.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/tickets`, {
        ...form,
        phone: form.phone.trim() || null,
        branch: form.branch.trim() || null,
      });
      setCreatedTicket(data);
      setForm(initial);
      toast.success(`Ticket ${data.ticket_id} creado correctamente.`);
      trackEvent("ticket_created", {
        ticket_id: data.ticket_id,
        severity: data.severity,
        equipment: data.equipment,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "No pudimos crear el ticket. Intenta nuevamente.";
      toast.error(typeof detail === "string" ? detail : "Error al crear ticket.");
    } finally {
      setSending(false);
    }
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    const id = statusInput.trim().toUpperCase();
    if (!id) {
      setStatusErr("Ingresa un ID de ticket.");
      return;
    }
    setStatusLoading(true);
    setStatusErr(null);
    setStatusResult(null);
    try {
      const { data } = await axios.get(`${API}/tickets/${id}`);
      setStatusResult(data);
    } catch (err) {
      const code = err?.response?.status;
      setStatusErr(
        code === 404
          ? `No encontramos un ticket con ID "${id}".`
          : "Error consultando el ticket. Reintenta más tarde."
      );
    } finally {
      setStatusLoading(false);
    }
  };

  return (
    <main
      data-testid="soporte-page"
      className="min-h-screen bg-white font-body text-slate-800 antialiased"
    >
      {/* Slim header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-[88px] md:h-[96px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="soporte-back-home"
            className="flex items-center gap-3 group"
          >
            <ArrowLeft className="w-4 h-4 text-slate-500 group-hover:text-[#0A2A4E] transition-colors" />
            <img src={LOGO_URL} alt="Betec" className="h-12 md:h-14 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] font-mono">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#2bc9ae] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2bc9ae]" />
            </span>
            <span className="text-slate-600 font-semibold">
              Mesa de ayuda · En línea
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-[#05162a] text-white pt-[120px] md:pt-[140px] pb-14 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage:
              "radial-gradient(ellipse 80% 70% at 50% 40%, black 50%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 80% 70% at 50% 40%, black 50%, transparent 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute -top-20 right-1/4 w-[480px] h-[480px] rounded-full bg-[#1FA08A]/15 blur-[140px]"
        />

        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-5 text-[11px] uppercase tracking-[0.32em] font-mono">
            <span className="text-[#2bc9ae] font-semibold">/ Soporte</span>
            <span className="h-px w-10 bg-white/25" />
            <span className="text-slate-300/70 font-semibold">
              Portal Betec · Mesa de Ayuda
            </span>
          </div>
          <h1
            data-testid="soporte-heading"
            className="font-heading text-white text-4xl sm:text-5xl md:text-[60px] leading-[1.04] tracking-tighter font-bold max-w-3xl"
          >
            Levanta un ticket.{" "}
            <span className="text-[#2bc9ae]">Recibimos. Operamos.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-200/85 leading-relaxed">
            Registra una incidencia o solicitud de soporte. Asignamos un
            técnico Betec según severidad y te entregamos un código único de
            seguimiento.
          </p>

          {/* Tabs */}
          <div
            data-testid="soporte-tabs"
            className="mt-10 inline-flex p-1 rounded-sm bg-white/[0.05] border border-white/10"
          >
            <button
              type="button"
              data-testid="tab-create"
              onClick={() => setMode("create")}
              className={`px-5 py-2 rounded-sm text-[12.5px] uppercase tracking-[0.18em] font-mono font-semibold transition-all ${
                mode === "create"
                  ? "bg-white text-[#0A2A4E]"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              Crear ticket
            </button>
            <button
              type="button"
              data-testid="tab-check"
              onClick={() => setMode("check")}
              className={`px-5 py-2 rounded-sm text-[12.5px] uppercase tracking-[0.18em] font-mono font-semibold transition-all ${
                mode === "check"
                  ? "bg-white text-[#0A2A4E]"
                  : "text-slate-300 hover:text-white"
              }`}
            >
              Consultar estado
            </button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          {mode === "create" ? (
            createdTicket ? (
              <TicketCreatedCard
                ticket={createdTicket}
                onReset={() => setCreatedTicket(null)}
              />
            ) : (
              <TicketForm
                form={form}
                errors={errors}
                sending={sending}
                onChange={handleChange}
                onSubmit={handleSubmit}
              />
            )
          ) : (
            <StatusChecker
              value={statusInput}
              onChange={(v) => {
                setStatusInput(v);
                if (statusErr) setStatusErr(null);
              }}
              onSubmit={handleCheckStatus}
              result={statusResult}
              error={statusErr}
              loading={statusLoading}
            />
          )}

          {/* Side note */}
          <div className="mt-12 border-t border-slate-200 pt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-[13px] text-slate-600">
            <div className="flex items-start gap-3">
              <Headphones className="w-4 h-4 text-[#1FA08A] mt-0.5 shrink-0" />
              <span>
                Para emergencias críticas <b>fuera de horario</b>, contáctanos
                directo por WhatsApp:{" "}
                <a
                  href={`https://wa.me/${BETEC_CONTACT.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0A2A4E] font-semibold hover:underline"
                >
                  abrir chat
                </a>
                .
              </span>
            </div>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-[#1FA08A] mt-0.5 shrink-0" />
              <span>
                Asigna la <b>severidad correcta</b>: tickets críticos se
                atienden antes en cola.
              </span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-[#1FA08A] mt-0.5 shrink-0" />
              <span>
                Guarda tu <b>código BTC-XXXXXX</b> para consultar el estado
                cuando quieras.
              </span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// ============== Subcomponents ==============

const TicketForm = ({ form, errors, sending, onChange, onSubmit }) => (
  <form
    data-testid="ticket-form"
    onSubmit={onSubmit}
    noValidate
    className="bg-white rounded-md border border-slate-200 p-6 md:p-10 shadow-[0_24px_60px_-40px_rgba(10,42,78,0.4)]"
  >
    <div className="text-[11px] uppercase tracking-[0.28em] font-mono font-semibold text-[#1FA08A] mb-2">
      Detalle de la incidencia
    </div>
    <h2 className="font-heading text-[#0A2A4E] text-2xl md:text-3xl font-semibold tracking-tight mb-8">
      Nuevo ticket de soporte
    </h2>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
      <SoporteField
        label="Tu nombre"
        name="requester_name"
        value={form.requester_name}
        onChange={onChange}
        error={errors.requester_name}
        required
        testid="ticket-name"
        placeholder="Nombre completo"
      />
      <SoporteField
        label="Empresa"
        name="company"
        value={form.company}
        onChange={onChange}
        error={errors.company}
        required
        testid="ticket-company"
        placeholder="Razón social u organización"
      />
      <SoporteField
        label="Correo electrónico"
        type="email"
        name="email"
        value={form.email}
        onChange={onChange}
        error={errors.email}
        required
        testid="ticket-email"
        placeholder="tu@empresa.cl"
      />
      <SoporteField
        label="Teléfono"
        name="phone"
        value={form.phone}
        onChange={onChange}
        testid="ticket-phone"
        placeholder="+56 9 ..."
      />
      <SoporteField
        label="Sucursal / Local"
        name="branch"
        value={form.branch}
        onChange={onChange}
        testid="ticket-branch"
        placeholder="Ej: Local 12 - Stgo Centro"
      />
      <div>
        <label
          htmlFor="equipment"
          className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2"
        >
          Equipo afectado
        </label>
        <div className="relative">
          <select
            id="equipment"
            name="equipment"
            data-testid="ticket-equipment"
            value={form.equipment}
            onChange={onChange}
            className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 pr-10 text-[15px] text-slate-900 focus:outline-none focus:border-[#1FA08A] focus:bg-white transition-colors"
          >
            {EQUIPMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span
            aria-hidden
            className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-2 h-2 border-r-2 border-b-2 border-slate-400 rotate-45"
          />
        </div>
      </div>

      {/* Severity selector */}
      <div className="md:col-span-2">
        <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
          Severidad <span className="text-[#1FA08A]">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          {SEVERITY_OPTIONS.map((s) => (
            <label
              key={s.value}
              data-testid={`severity-${s.value}`}
              className={`relative cursor-pointer rounded-sm border p-3 transition-all ${
                form.severity === s.value
                  ? "border-[#1FA08A] bg-[#1FA08A]/[0.06] shadow-[inset_0_0_0_1px_#1FA08A]"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <input
                type="radio"
                name="severity"
                value={s.value}
                checked={form.severity === s.value}
                onChange={onChange}
                className="sr-only"
              />
              <div className="flex items-center gap-2 mb-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <span className="text-[13px] font-semibold text-[#0A2A4E]">
                  {s.label}
                </span>
              </div>
              <span className="text-[11.5px] text-slate-500 leading-snug">
                {s.sub}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="md:col-span-2">
        <SoporteField
          label="Asunto"
          name="subject"
          value={form.subject}
          onChange={onChange}
          error={errors.subject}
          required
          testid="ticket-subject"
          placeholder="Ej: POS no imprime boleta en caja 3"
        />
      </div>

      <div className="md:col-span-2">
        <label
          htmlFor="description"
          className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2"
        >
          Descripción del problema{" "}
          <span className="text-[#1FA08A]">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          data-testid="ticket-description"
          rows={6}
          value={form.description}
          onChange={onChange}
          placeholder="Detalla lo que ocurre, desde cuándo, qué intentaste, mensajes de error visibles, número de serie si lo tienes, etc."
          className={`w-full bg-slate-50 border rounded-sm px-4 py-3 text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors resize-none ${
            errors.description
              ? "border-rose-400 focus:border-rose-500"
              : "border-slate-200 focus:border-[#1FA08A]"
          }`}
        />
        {errors.description && (
          <p className="mt-1.5 text-[12px] text-rose-500">{errors.description}</p>
        )}
      </div>

      <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-2">
        <button
          type="submit"
          data-testid="ticket-submit"
          disabled={sending}
          className="cta-cotizar group inline-flex items-center justify-center gap-2.5 bg-[#0A2A4E] hover:bg-[#153d6e] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-3.5 rounded-sm font-semibold text-[14px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-md"
        >
          {sending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando ticket...
            </>
          ) : (
            <>
              Crear ticket
              <span aria-hidden className="cta-dot" />
              <Send className="w-4 h-4" />
            </>
          )}
        </button>
        <span className="text-[12px] text-slate-500">
          Te enviaremos el código BTC-XXXXXX a tu correo.
        </span>
      </div>
    </div>
  </form>
);

const SoporteField = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  type = "text",
  testid,
  placeholder,
}) => (
  <div>
    <label
      htmlFor={name}
      className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2"
    >
      {label} {required && <span className="text-[#1FA08A]">*</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className={`w-full bg-slate-50 border rounded-sm px-4 py-3 text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors ${
        error
          ? "border-rose-400 focus:border-rose-500"
          : "border-slate-200 focus:border-[#1FA08A]"
      }`}
    />
    {error && <p className="mt-1.5 text-[12px] text-rose-500">{error}</p>}
  </div>
);

const TicketCreatedCard = ({ ticket, onReset }) => {
  const copy = () => {
    navigator.clipboard?.writeText(ticket.ticket_id);
    toast.success("Código de ticket copiado al portapapeles.");
  };
  return (
    <div
      data-testid="ticket-created"
      className="bg-white rounded-md border border-[#1FA08A]/30 p-6 md:p-10 shadow-[0_24px_60px_-40px_rgba(10,42,78,0.4)] relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full bg-[#1FA08A]/[0.06] blur-[80px]"
      />
      <div className="relative">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-md bg-[#1FA08A]/15 text-[#1FA08A] mb-6">
          <CheckCircle2 className="w-8 h-8" strokeWidth={1.5} />
        </div>
        <div className="text-[11px] uppercase tracking-[0.28em] font-mono font-semibold text-[#1FA08A] mb-2">
          Ticket creado
        </div>
        <h2 className="font-heading text-[#0A2A4E] text-2xl md:text-3xl font-semibold tracking-tight mb-2">
          Recibimos tu solicitud.
        </h2>
        <p className="text-slate-600 text-[15px] leading-relaxed mb-8 max-w-2xl">
          Asignaremos un técnico Betec según severidad y nos pondremos en
          contacto al correo que registraste. Guarda este código para
          consultar el estado en cualquier momento.
        </p>

        {/* Ticket code */}
        <div className="rounded-md bg-slate-50 border border-slate-200 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono font-semibold text-slate-500 mb-1">
              Código de ticket
            </div>
            <div
              data-testid="ticket-id"
              className="font-mono text-2xl md:text-3xl font-bold text-[#0A2A4E] tracking-tight"
            >
              {ticket.ticket_id}
            </div>
          </div>
          <button
            type="button"
            data-testid="ticket-copy"
            onClick={copy}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-[#1FA08A] text-[#0A2A4E] px-4 py-2 rounded-sm text-[13px] font-semibold transition-colors"
          >
            <Copy className="w-3.5 h-3.5" />
            Copiar
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-[13px] text-slate-700 mb-8">
          <Info label="Severidad" value={ticket.severity} cap />
          <Info label="Equipo" value={ticket.equipment} cap />
          <Info label="Estado" value={ticket.status} cap />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center justify-center gap-2 bg-[#0A2A4E] hover:bg-[#153d6e] text-white px-6 py-3 rounded-sm font-semibold text-[13px] uppercase tracking-[0.16em] transition-colors"
          >
            Crear otro ticket
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-[#0A2A4E] px-6 py-3 rounded-sm font-semibold text-[13px] uppercase tracking-[0.16em] transition-colors"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

const Info = ({ label, value, cap }) => (
  <div className="border-l-2 border-[#1FA08A] pl-3">
    <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono font-semibold text-slate-500 mb-0.5">
      {label}
    </div>
    <div className={`font-semibold text-[#0A2A4E] ${cap ? "capitalize" : ""}`}>
      {value}
    </div>
  </div>
);

const StatusChecker = ({ value, onChange, onSubmit, result, error, loading }) => (
  <div className="bg-white rounded-md border border-slate-200 p-6 md:p-10 shadow-[0_24px_60px_-40px_rgba(10,42,78,0.4)]">
    <div className="text-[11px] uppercase tracking-[0.28em] font-mono font-semibold text-[#1FA08A] mb-2">
      Seguimiento
    </div>
    <h2 className="font-heading text-[#0A2A4E] text-2xl md:text-3xl font-semibold tracking-tight mb-6">
      Consultar estado de un ticket
    </h2>
    <form
      data-testid="ticket-status-form"
      onSubmit={onSubmit}
      className="flex flex-col sm:flex-row gap-3 mb-6"
    >
      <input
        data-testid="ticket-status-input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="BTC-XXXXXX"
        className="flex-1 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-[15px] font-mono uppercase tracking-wider text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#1FA08A] focus:bg-white transition-colors"
      />
      <button
        type="submit"
        data-testid="ticket-status-submit"
        disabled={loading}
        className="inline-flex items-center justify-center gap-2 bg-[#0A2A4E] hover:bg-[#153d6e] disabled:opacity-60 text-white px-6 py-3 rounded-sm font-semibold text-[13px] uppercase tracking-[0.16em] transition-colors"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        Consultar
      </button>
    </form>

    {error && (
      <div
        data-testid="ticket-status-error"
        className="rounded-sm border border-rose-200 bg-rose-50 text-rose-700 text-[14px] px-4 py-3"
      >
        {error}
      </div>
    )}

    {result && (
      <div data-testid="ticket-status-result" className="rounded-md bg-slate-50 border border-slate-200 p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono font-semibold text-slate-500 mb-0.5">
              Ticket
            </div>
            <div className="font-mono text-2xl font-bold text-[#0A2A4E]">
              {result.ticket_id}
            </div>
          </div>
          <StatusBadge status={result.status} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[14px] text-slate-700">
          <Info label="Empresa" value={result.company} />
          <Info label="Equipo" value={result.equipment} cap />
          <Info label="Severidad" value={result.severity} cap />
          <Info
            label="Creado"
            value={new Date(result.created_at).toLocaleString("es-CL")}
          />
        </div>
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono font-semibold text-slate-500 mb-1">
            Asunto
          </div>
          <div className="text-[#0A2A4E] font-semibold mb-3">{result.subject}</div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-mono font-semibold text-slate-500 mb-1">
            Descripción
          </div>
          <p className="text-slate-700 text-[14px] leading-relaxed whitespace-pre-line">
            {result.description}
          </p>
        </div>
      </div>
    )}
  </div>
);

const STATUS_LABELS = {
  open: { label: "Abierto", color: "bg-[#1FA08A]" },
  in_progress: { label: "En progreso", color: "bg-amber-500" },
  resolved: { label: "Resuelto", color: "bg-emerald-600" },
  closed: { label: "Cerrado", color: "bg-slate-500" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || STATUS_LABELS.open;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white border border-slate-200 text-[12px] font-semibold uppercase tracking-[0.16em]"
    >
      <span className={`w-2 h-2 rounded-full ${s.color}`} />
      <span className="text-[#0A2A4E]">{s.label}</span>
    </span>
  );
};
